import { Brain, ChevronDown, ChevronRight, Loader2, Wrench } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import type { Thought } from "@/types";

interface ThoughtIndicatorProps {
  thoughts: Thought[];
  defaultOpen?: boolean;
}

/**
 * ThoughtIndicator (P3 fix) — luôn auto-mở khi đang chạy, auto-collapse
 * khi hoàn thành. User có thể click toggle bất kỳ lúc nào.
 */
export function ThoughtIndicator({
  thoughts,
  defaultOpen,
}: ThoughtIndicatorProps): JSX.Element | null {
  const isRunning = thoughts.some((t) => t.status === "running");

  // Auto-open khi running, auto-close khi all done. User override sticks.
  const [userToggled, setUserToggled] = React.useState(false);
  const [openOverride, setOpenOverride] = React.useState<boolean | null>(null);

  const computedOpen =
    defaultOpen !== undefined
      ? defaultOpen
      : userToggled && openOverride !== null
        ? openOverride
        : isRunning; // default: open while running, collapse when done

  if (thoughts.length === 0) return null;

  const lastThought = thoughts[thoughts.length - 1];
  if (!lastThought) return null;

  const handleToggle = (): void => {
    setUserToggled(true);
    setOpenOverride(!computedOpen);
  };

  const totalDurationMs = thoughts.reduce((sum, t) => {
    if (t.endedAt) return sum + (t.endedAt - t.startedAt);
    if (t.status === "running") return sum + (Date.now() - t.startedAt);
    return sum;
  }, 0);

  return (
    <div className="pl-8 mb-2 animate-fade-in">
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-md transition-colors",
          isRunning
            ? "bg-gradient-brand-soft text-foreground hover:bg-gradient-brand-soft/80"
            : "bg-muted text-muted-foreground hover:bg-muted/70",
        )}
        onClick={handleToggle}
        aria-expanded={computedOpen}
      >
        {isRunning ? (
          <Loader2 className="size-3 animate-spin text-primary" />
        ) : lastThought.tool ? (
          <Wrench className="size-3" />
        ) : (
          <Brain className="size-3" />
        )}
        <span className="truncate max-w-[280px]">{lastThought.step}</span>
        {!isRunning ? (
          <span className="text-[10px] opacity-70">
            {(totalDurationMs / 1000).toFixed(1)}s · {thoughts.length} bước
          </span>
        ) : null}
        {computedOpen ? (
          <ChevronDown className="size-3" />
        ) : (
          <ChevronRight className="size-3" />
        )}
      </button>

      {computedOpen ? (
        <ul className="mt-2 space-y-1 pl-4 border-l border-muted-foreground/20">
          {thoughts.map((t) => (
            <li
              key={t.id}
              className="text-xs flex items-start gap-2 py-0.5"
            >
              <span
                className={cn(
                  "size-1.5 rounded-full mt-1.5 shrink-0",
                  t.status === "running" && "bg-primary animate-pulse",
                  t.status === "done" && "bg-success",
                  t.status === "failed" && "bg-destructive",
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="text-muted-foreground">{t.step}</div>
                {t.tool ? (
                  <code className="text-[10px] text-muted-foreground/70">
                    {t.tool}
                  </code>
                ) : null}
              </div>
              {t.endedAt ? (
                <span className="text-[10px] text-muted-foreground/60 shrink-0">
                  {((t.endedAt - t.startedAt) / 1000).toFixed(1)}s
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
